import { Button } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';

import type { PolicyModel } from 'generated/types';

import PolicyForm from '../Create/PolicyForm';

const messages = defineMessages({
  name: {
    id: 'admin.policies.permissions.name',
    defaultMessage: 'Name',
  },
  scope: {
    id: 'admin.policies.permissions.scope',
    defaultMessage: 'Scope',
  },
  accessLevel: {
    id: 'admin.policies.permissions.accessLevel',
    defaultMessage: 'Access Level',
  },
  noData: {
    id: 'admin.policies.permissions.noData',
    defaultMessage: 'No permissions',
  },
  isEnabled: {
    id: 'admin.policies.permissions.isEnabled',
    defaultMessage: 'Enabled',
  },
  yes: {
    id: 'GLOBAL.yes',
    defaultMessage: 'Yes',
  },
  no: {
    id: 'GLOBAL.no',
    defaultMessage: 'No',
  },
});

interface PermissionListProps {
  policy?: PolicyModel;
  loading: boolean;
}
interface RowData {
  id: string;
  scope: string;
  name: string;
  is_enabled: boolean;
  access_level?: string | null;
}

function PermissionList({ policy, loading }: PermissionListProps) {
  const data = policy?.permissions;

  const { formatMessage } = useIntl();
  const [isEditing, setIsEditing] = React.useState(false);

  const tableData = React.useMemo(
    () =>
      (data ?? []).map<RowData>(
        ({
          permission_id,
          permission_name,
          permission_scope,
          access_level,
          is_enabled,
        }) => {
          return {
            id: permission_id,
            scope: permission_scope,
            name: permission_name,
            access_level,
            is_enabled,
          };
        },
        []
      ),
    [data]
  );

  const handleEditClick = () => {
    setIsEditing(!isEditing);
  };

  return (
    <ContentLoader loading={loading}>
      {!isEditing && (
        <MuiDataTable
          columns={[
            {
              name: 'scope',
              className: 'small',
              label: formatMessage(messages.scope),
              options: {
                searchable: true,
                sort: true,
              },
            },
            {
              name: 'name',
              className: 'blue-hover',
              label: formatMessage(messages.name),
              options: {
                searchable: true,
                sort: true,
              },
            },
            {
              name: 'is_enabled',
              className: 'small-light',
              label: formatMessage(messages.isEnabled),
              options: {
                filter: true,
                sort: true,
                customBodyRender: (value: RowData['is_enabled']) =>
                  value
                    ? formatMessage(messages.yes)
                    : formatMessage(messages.no),
              },
            },
            {
              name: 'access_level',
              className: 'small-light',
              label: formatMessage(messages.accessLevel),
              options: {
                filter: true,
                sort: true,
                customBodyRender: (value: RowData['access_level']) => value,
              },
            },
          ]}
          data={tableData}
          options={{
            search: true,
            sort: true,
            filter: true,
            sortOrder: {
              name: 'name',
              direction: 'asc',
            },
            textLabels: {
              body: {
                noMatch: formatMessage(messages.noData),
              },
            },
            customToolbar: () => {
              return (
                <Button color="secondary" onClick={handleEditClick}>
                  {formatMessage({ id: 'GLOBAL.edit' })}
                </Button>
              );
            },
          }}
        />
      )}
      {isEditing && (
        <PolicyForm policy={policy} onCancelEdit={handleEditClick} />
      )}
    </ContentLoader>
  );
}

export default PermissionList;
